import { AvField, AvForm } from "availity-reactstrap-validation";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";
import api from "../../services/api";
import { setBreadcrumbItems } from "../../store/actions";


const SwapStudentCourseRegistration = (props) => {
  const navigate = useNavigate();
  document.title = "Technical Hub | Swap Paid Student Technology";
  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Student Registrations", link: "#" },
    { title: "Swap Paid Student Technology", link: "#" },
  ];

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [courses, setCourses] = useState([]);

  // FROM side
  const [selectedFromCourse, setSelectedFromCourse] = useState(null);
  const [fromTechnologies, setFromTechnologies] = useState([]);
  const [selectedFromTechnology, setSelectedFromTechnology] = useState(null);

  // TO side
  const [selectedToCourse, setSelectedToCourse] = useState(null);
  const [toTechnologies, setToTechnologies] = useState([]);
  const [selectedToTechnology, setSelectedToTechnology] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCourses();
    document.title = "Technical Hub | Swap Paid Student Technology";
  }, []);

  /** Fetch all available courses */
  const fetchCourses = async () => {
    try {
      const { data } = await axios.get(`${backendUrl}get-courses`);
      setCourses(data || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Failed to load courses.");
    }
  };

  /** Fetch technologies by course ID */
  const fetchTechnologiesByCourse = async (courseId) => {
    try {
      const response = await axios.post(
        `${backendUrl}get-technology-by-course-id`,
        { course_id: courseId }
      );
      return response.data.Technology || response.data.technologies || response.data || [];
    } catch (error) {
      console.error("Error fetching technologies:", error);
      toast.error("Failed to load technologies.");
      return [];
    }
  };

  /** When From Course changes */
  useEffect(() => {
    if (!selectedFromCourse) {
      setFromTechnologies([]);
      setSelectedFromTechnology(null);
      return;
    }
    (async () => {
      const techs = await fetchTechnologiesByCourse(selectedFromCourse.value);
      setFromTechnologies(techs);
      setSelectedFromTechnology(null);
    })();
  }, [selectedFromCourse]);

  /** When To Course changes */
  useEffect(() => {
    if (!selectedToCourse) {
      setToTechnologies([]);
      setSelectedToTechnology(null);
      return;
    }
    (async () => {
      const techs = await fetchTechnologiesByCourse(selectedToCourse.value);
      setToTechnologies(techs);
      setSelectedToTechnology(null);
    })();
  }, [selectedToCourse]);

  const courseOptions = courses.map((c) => ({
    value: c._id,
    label: c.course_name,
  }));

  const buildTechOptions = (techs) =>
    techs.map((t) => ({
      value: t._id || t.technology_id,
      label: t.technology_name,
    }));

  /** Reset entire form */
  const resetForm = (formRef) => {
    setSelectedFromCourse(null);
    setSelectedFromTechnology(null);
    setFromTechnologies([]);
    setSelectedToCourse(null);
    setSelectedToTechnology(null);
    setToTechnologies([]);
    if (formRef) formRef.reset();
  };

  /** Submit Handler */
  const handleSubmit = async (e, values) => {
    if (
      !selectedFromCourse ||
      !selectedFromTechnology ||
      !selectedToCourse ||
      !selectedToTechnology
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (
      selectedFromCourse.value === selectedToCourse.value &&
      selectedFromTechnology.value === selectedToTechnology.value
    ) {
      toast.error("From and To selections cannot be the same.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        roll_no: values.rollNo.toUpperCase().trim(),
        from_course_id: selectedFromCourse.value,
        from_technology_id: selectedFromTechnology.value,
        to_course_id: selectedToCourse.value,
        to_technology_id: selectedToTechnology.value,
      };

      const res = await api.post(
        `${backendUrl}swapping-student-technology-after-paid`,
        payload
      );

      if (res.status === 200) {
        toast.success("Paid student technology swapped successfully."); resetForm(e.target);
        setTimeout(() => navigate("/student-registrations-list"), 1500);
      }
    } catch (error) {
      console.error("Swap error:", error);
      const msg = error.response?.data?.message || "Swap failed. Please try again.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          transition={Bounce}
          theme="light"
        />

        <Col xl="12">
          <Card>
            <CardBody>
              <h4 className="card-title mb-4">
                Swap Paid Student Technology
              </h4>
              <AvForm onValidSubmit={handleSubmit}>
                {/* Roll No */}
                <Row>
                  <Col md="6">
                    <div className="mb-4">
                      <Label>
                        Roll No <span className="text-danger">*</span>
                      </Label>
                      <AvField
                        name="rollNo"
                        placeholder="Enter Roll No (e.g. 24P31A1236)"
                        validate={{ required: { value: true, errorMessage: "Roll No is required" } }}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Divider */}
                <hr className="my-3" />

                <Row>
                  {/* FROM Section */}
                  <Col md="6">
                    <h5 className="mb-3 text-muted fw-semibold">
                      <i className="mdi mdi-arrow-right-circle me-1 text-danger" />
                      From
                    </h5>

                    {/* From Course */}
                    <div className="mb-4">
                      <Label>
                        From Course <span className="text-danger">*</span>
                      </Label>
                      <Select
                        value={selectedFromCourse}
                        onChange={(option) => {
                          setSelectedFromCourse(option);
                        }}
                        options={courseOptions}
                        placeholder="Select current course"
                        isClearable
                      />
                    </div>

                    {/* From Technology */}
                    <div className="mb-4">
                      <Label>
                        From Technology <span className="text-danger">*</span>
                      </Label>
                      <Select
                        value={selectedFromTechnology}
                        onChange={setSelectedFromTechnology}
                        options={buildTechOptions(fromTechnologies)}
                        isDisabled={!selectedFromCourse}
                        placeholder={
                          selectedFromCourse
                            ? "Select current technology"
                            : "Select a course first"
                        }
                        isClearable
                      />
                    </div>
                  </Col>

                  {/* TO Section */}
                  <Col md="6">
                    <h5 className="mb-3 text-muted fw-semibold">
                      <i className="mdi mdi-arrow-left-circle me-1 text-success" />
                      To
                    </h5>

                    {/* To Course */}
                    <div className="mb-4">
                      <Label>
                        To Course <span className="text-danger">*</span>
                      </Label>
                      <Select
                        value={selectedToCourse}
                        onChange={(option) => {
                          setSelectedToCourse(option);
                        }}
                        options={courseOptions}
                        placeholder="Select target course"
                        isClearable
                      />
                    </div>

                    {/* To Technology */}
                    <div className="mb-4">
                      <Label>
                        To Technology <span className="text-danger">*</span>
                      </Label>
                      <Select
                        value={selectedToTechnology}
                        onChange={setSelectedToTechnology}
                        options={buildTechOptions(toTechnologies)}
                        isDisabled={!selectedToCourse}
                        placeholder={
                          selectedToCourse
                            ? "Select target technology"
                            : "Select a course first"
                        }
                        isClearable
                      />
                    </div>
                  </Col>
                </Row>

                <hr className="my-3" />

                <div className="d-flex gap-2">
                  <Button
                    color="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Swapping..." : "Swap Paid Student"}                  </Button>
                  <Button
                    color="secondary"
                    type="button"
                    outline
                    onClick={() => resetForm(null)}
                  >
                    Reset
                  </Button>
                </div>
              </AvForm>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(SwapStudentCourseRegistration);